import catalogue from '../../../app-config/app_data/permissions.json';

export type Permission = keyof typeof catalogue.permissions;

// The nodeStream company's id, which install fixes; never matched by name, which any company could take
export const NODESTREAM_COMPANY_ID = '69ba66495f8f479e3aec78d0';

export interface RoleAssignmentRow {
  roleId: string | null;
  personId: string | null;
  companyId: string | null;
}

export interface RoleRow {
  id: string;
  companyId: string | null;
  permissions: string[];
  kind?: string | null;
  systemKey?: string | null;
}

export function isPermission(value: string): value is Permission {
  return Object.prototype.hasOwnProperty.call(catalogue.permissions, value);
}

const isPlatformKey = (key: string | null | undefined) =>
  !!key && Object.entries(catalogue.systemRoles).some(([name, role]) => name === key && role.nodeStreamOnly);

// The permissions of the features the company lacks, which count for nobody there (D16)
export function lackedPermissions(features: readonly string[] | null | undefined): Set<string> {
  return new Set(
    Object.entries(catalogue.features)
      .filter(([key]) => !(features ?? []).includes(key))
      .flatMap(([, feature]) => feature.permissions),
  );
}

// What the person may do in their own company, within its features, as the lambdas' permissionsOf works it out; the server
// still decides
export function permissionsFrom(
  person: { id: string; companyId?: string | null; employed?: boolean | null } | undefined,
  assignments: RoleAssignmentRow[],
  roles: RoleRow[],
  features: readonly string[] | null | undefined,
): Set<Permission> {
  const granted = new Set<Permission>();
  if (!person?.companyId || person.employed === false) return granted;
  const roleIds = new Set(
    assignments.filter((a) => a.personId === person.id && a.companyId === person.companyId).map((a) => a.roleId),
  );
  for (const role of roles) {
    if (role.companyId !== person.companyId || !roleIds.has(role.id)) continue;
    // Platform permissions come only from nodeStream's own platform roles (D3)
    const platformRole =
      role.companyId === NODESTREAM_COMPANY_ID && role.kind === 'SYSTEM' && isPlatformKey(role.systemKey);
    role.permissions
      .filter(isPermission)
      .filter((p) => platformRole || catalogue.permissions[p].scope === 'COMPANY')
      .forEach((p) => granted.add(p));
  }
  const lacked = lackedPermissions(features);
  return new Set([...granted].filter((permission) => !lacked.has(permission)));
}
